/*
 * Booking → CalendarEvent mappers shared by admin/availability (Team Schedule)
 * and staff/schedule (My Schedule). Both surfaces render the same shape so the
 * unified <Calendar> displays bookings identically across views.
 *
 * Bookings without a slot (availability-based) still need a placeholder
 * duration; 30 min is the established default used by admin/bookings.
 */

import { addMinutes, format, parseISO } from 'date-fns';
import type { Booking } from '@/types/booking';
import type { BookingStatus } from '@/lib/status-colors';
import type { CalendarEvent, EventStatus } from './types';

const DEFAULT_DURATION_MIN = 30;

const BOOKING_STATUS_TO_EVENT: Record<string, EventStatus> = {
  pending: 'pending',
  confirmed: 'confirmed',
  completed: 'done',
  cancelled: 'cancelled',
  no_show: 'blocked',
};

export interface BookingEventInput {
  id: number | string;
  start: Date;
  end: Date;
  customerName: string;
  serviceName?: string;
  status: BookingStatus | string;
  /** Column to render in (week/team view). Defaults to yyyy-MM-dd of start. */
  columnId?: string;
  meta?: Record<string, unknown>;
}

/** Single source of truth for shape of a booking CalendarEvent. */
export function buildBookingEvent(input: BookingEventInput): CalendarEvent {
  return {
    id: `booking-${input.id}`,
    start: input.start,
    end: input.end,
    title: input.customerName,
    subtitle: input.serviceName,
    status: BOOKING_STATUS_TO_EVENT[input.status] ?? 'confirmed',
    columnId: input.columnId ?? format(input.start, 'yyyy-MM-dd'),
    meta: input.meta,
  };
}

function getStartTime(booking: Booking): string | undefined {
  return booking.availableSlot?.startTime || booking.start_time;
}

function getEndTime(booking: Booking): string | undefined {
  return booking.availableSlot?.endTime || booking.end_time;
}

function bookingDurationMin(booking: Booking): number {
  const slot = booking.availableSlot;
  if (slot?.startTime && slot?.endTime) {
    return Math.max(15, (parseISO(slot.endTime).getTime() - parseISO(slot.startTime).getTime()) / 60000);
  }
  return DEFAULT_DURATION_MIN;
}

function getMaxCapacity(booking: Booking): number {
  const svc = booking.service as any;
  const slotSvc = booking.availableSlot?.service as any;
  const slot = booking.availableSlot as any;
  return (
    slot?.maxCapacity ??
    slot?.max_capacity ??
    svc?.max_capacity ??
    svc?.maxCapacity ??
    slotSvc?.max_capacity ??
    slotSvc?.maxCapacity ??
    1
  );
}

function getSlotId(booking: Booking): number | null {
  return booking.availableSlotId ?? booking.availableSlot?.id ?? null;
}

function getCustomerName(booking: Booking): string {
  const b = booking as any;
  if (b.customer_full_name) return b.customer_full_name;
  const first = booking.customerFirstName ?? b.customer_first_name ?? '';
  const last = booking.customerLastName ?? b.customer_last_name ?? '';
  return `${first} ${last}`.trim();
}

export function bookingToEvent(booking: Booking): CalendarEvent | null {
  const startTime = getStartTime(booking);
  if (!startTime) return null;
  const start = parseISO(startTime);
  const endTime = getEndTime(booking);
  const end = endTime ? parseISO(endTime) : addMinutes(start, bookingDurationMin(booking));
  return buildBookingEvent({
    id: booking.id,
    start,
    end,
    customerName: getCustomerName(booking),
    serviceName: booking.availableSlot?.service?.name || booking.service?.name,
    status: booking.status,
    meta: { kind: 'booking', booking },
  });
}

// Collapse group-class bookings (max_capacity > 1) into one event per slot so
// the calendar shows "Pilates Class · 3/6" rather than N stacked participants.
export function bookingsToEvents(bookings: Booking[]): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  const groups = new Map<number, Booking[]>();

  for (const b of bookings) {
    const cap = getMaxCapacity(b);
    const slotId = getSlotId(b);
    if (cap > 1 && slotId != null) {
      const list = groups.get(slotId) ?? [];
      list.push(b);
      groups.set(slotId, list);
      continue;
    }
    const e = bookingToEvent(b);
    if (e) out.push(e);
  }

  for (const [slotId, bs] of groups.entries()) {
    const first = bs[0];
    const startTime = getStartTime(first);
    if (!startTime) continue;
    const start = parseISO(startTime);
    const endTime = getEndTime(first);
    const end = endTime ? parseISO(endTime) : addMinutes(start, bookingDurationMin(first));
    const slot = first.availableSlot as any;
    const cap = getMaxCapacity(first);
    const slotCurrent = slot?.currentBookings ?? slot?.current_bookings;
    const count = typeof slotCurrent === 'number' ? slotCurrent : bs.length;
    const serviceName =
      first.availableSlot?.service?.name || first.service?.name || 'Group Booking';
    out.push({
      id: `group-${slotId}`,
      start,
      end,
      title: serviceName,
      subtitle: `${count}/${cap}`,
      status: 'group-class',
      columnId: format(start, 'yyyy-MM-dd'),
      meta: { kind: 'group-class', groupSlotId: slotId, bookings: bs },
    });
  }

  return out;
}
